import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import Permissioned from 'dashboard/components/Permissioned';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import { VerificationMobileDriversLicense } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Sticker from 'design-system/components/Sticker';
import { Heading } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { Permission } from 'lib/permissions';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const ChecksInformationRow = styled('div')`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

interface MobileDriversLicenseVerificationProps extends VerificationDetailProps {
  verification: VerificationMobileDriversLicense;
}

const ChecksHeaderContainer = styled('div')`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  padding: 0 ${Spacing[3]};
`;

const TitleContainer = styled('div')`
  justify-self: start;
`;

const ImageGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${Spacing[4]};
  padding-top: ${Spacing[2]};
`;

const PhotoLabel = styled.p`
  ${paragraphMixins.lgBold};
  margin: ${Spacing[2]} 0;
`;

const ChecksHeader = ({ title }: { title: string }) => {
  return (
    <ChecksHeaderContainer>
      <TitleContainer>
        <Heading size='sm'>{title}</Heading>
      </TitleContainer>
    </ChecksHeaderContainer>
  );
};

const MobileDriversLicenseVerification = ({
  verification,
}: MobileDriversLicenseVerificationProps) => {
  const {
    'name-first': nameFirst,
    'name-last': nameLast,
    'address-street': addressStreet,
    'address-city': addressCity,
    'address-state': addressState,
    'address-postal-code': addressPostalCode,
    'identification-number': identificationNumber,
    'expiration-date': expirationDate,
    // 'issuing-jurisdiction': issuingJurisdiction,
    // 'issuing-authority': issuingAuthority,
    birthdate: birthdate,
    age: age,
    // 'age-is-at-least-age': ageIsAtLeastAge,
    'portrait-photo-url': portraitPhotoUrl,
    'redacted-at': redactedAt,
  } = verification.attributes;

  const isRedacted = redactedAt != null;

  const submitted = status !== VerificationStatus.Initiated;

  const checks = verification.attributes.checks;

  const fullName = [nameFirst, nameLast].filter(Boolean).join(' ');

  const address = addressStreet ? (
    <div>
      {addressStreet && <p>{addressStreet}</p>}
      {addressCity && addressState && addressPostalCode && (
        <p>
          {addressCity}, {addressState} {addressPostalCode}
        </p>
      )}
    </div>
  ) : (
    <div>-</div>
  );

  return (
    <ChecksInformationRow data-test='verification-detail__mdl-verification'>
      <ChecksHeader title='Collected Fields' />

      <StyledAttributeGrid>
        {fullName && (
          <Attribute
            label='Full name'
            value={titleCase(fullName)}
            redacted={isRedacted}
            placeholder='No name collected'
            data-test='mdl-verification__full-name'
          />
        )}
        {address && (
          <Attribute
            label='Address'
            value={address}
            redacted={isRedacted}
            placeholder='No address collected'
            data-test='mdl-verification__address'
          />
        )}
        {identificationNumber && (
          <Attribute
            label='ID number'
            value={
              <Permissioned
                permission={Permission.VerificationGovIdNumberView}
                fallback={
                  <Sticker variant='basic' data-test='verification-detail__mdl-id__not-available'>
                    Not Available
                  </Sticker>
                }
              >
                {identificationNumber}
              </Permissioned>
            }
            redacted={isRedacted}
            placeholder='No ID number collected'
            data-test='mdl-verification__identification-number'
          />
        )}
        {expirationDate && (
          <Attribute
            label='Expiration date'
            value={expirationDate}
            redacted={isRedacted}
            placeholder='No expiration date collected'
            data-test='mdl-verification__expiration-date'
          />
        )}
        {birthdate && (
          <Attribute
            label='Birthdate'
            value={birthdate}
            redacted={isRedacted}
            placeholder='No birthdate collected'
            data-test='mdl-verification__birthdate'
          />
        )}
        {age && (
          <Attribute
            label='Age'
            value={age}
            redacted={isRedacted}
            placeholder='No age collected'
            data-test='mdl-verification__age'
          />
        )}
      </StyledAttributeGrid>
      <StyledAttributeGrid>
        {portraitPhotoUrl && (
          <ImageGridContainer data-test='verification-detail__mdl-photos'>
            <div>
              <AsyncImage
                imageUrl={portraitPhotoUrl}
                width='initial'
                height='250px'
                modalLabel='Portrait photo'
                allowModalExpand={true}
              />
              <PhotoLabel>Portrait photo</PhotoLabel>
            </div>
          </ImageGridContainer>
        )}
      </StyledAttributeGrid>
      {submitted && checks && <ChecksTable checks={checks} />}
    </ChecksInformationRow>
  );
};

export default MobileDriversLicenseVerification;
